import {
  motion
} from "framer-motion";

// Three depth layers: back (lightest), mid, front (darkest)
const backBuildings = [
  {
    w: 70, h: 140, x: 30
  },
  {
    w: 80, h: 180, x: 150
  },
  {
    w: 65, h: 130, x: 300
  },
  {
    w: 75, h: 160, x: 420
  },
];

const midBuildings = [
  {
    w: 58, h: 200, x: 10
  },
  {
    w: 68, h: 260, x: 100
  },
  {
    w: 55, h: 190, x: 220
  },
  {
    w: 72, h: 280, x: 340
  },
  {
    w: 50, h: 170, x: 470
  },
];

const frontBuildings = [
  {
    w: 55, h: 180, x: 0
  },
  {
    w: 65, h: 250, x: 60
  },
  {
    w: 48, h: 160, x: 130
  },
  {
    w: 75, h: 300, x: 183
  },
  {
    w: 52, h: 210, x: 263
  },
  {
    w: 60, h: 270, x: 320
  },
  {
    w: 45, h: 150, x: 385
  },
  {
    w: 70, h: 320, x: 435
  },
  {
    w: 50, h: 190, x: 510
  },
];

const generateWindows = (b, windowSize = 11, gap = 18) => {
  const windows = [];
  const cols = Math.floor((b.w - 14) / gap);
  const rows = Math.floor((b.h - 20) / 28);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      windows.push([7 + c * gap, 12 + r * 28]);
    }
  }
  return windows;
};

const BuildingSkyline = () => {
  return (
    <div className="relative w-full h-full flex items-end justify-center">
      {/* Glow behind buildings */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-[120px]"
        style={{
          background: "hsl(263 60% 50% / 0.2)"
        }}
      />
      <svg viewBox="0 0 570 340" className="relative w-full max-w-[560px]">
        <defs>
          {/* Back layer - lightest purple */}
          <linearGradient id="buildingGradBack" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(263 40% 52%)" />
            <stop offset="100%" stopColor="hsl(263 42% 16%)" />
          </linearGradient>
          {/* Mid layer - medium purple */}
          <linearGradient id="buildingGradMid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(263 42% 42%)" />
            <stop offset="100%" stopColor="hsl(263 42% 16%)" />
          </linearGradient>
          {/* Front layer - darkest purple */}
          <linearGradient id="buildingGradFront" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(263 45% 35%)" />
            <stop offset="100%" stopColor="hsl(263 42% 12%)" />
          </linearGradient>

          {/* Mask to fade out the bottom of buildings */}
          <mask id="skylineBottomMask">
            <linearGradient id="maskGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="white" />
              <stop offset="80%" stopColor="white" />
              <stop offset="100%" stopColor="black" />
            </linearGradient>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#maskGrad)" />
          </mask>
        </defs>

        <g mask="url(#skylineBottomMask)">

        {/* Back buildings - lightest, shorter */}
        {backBuildings.map((b, i) => (
          <g key={`back-${i}`}>
            <rect
              x={b.x}
              y={340 - b.h}
              width={b.w}
              height={b.h}
              fill="url(#buildingGradBack)"
              rx="2"
              stroke="hsl(263 50% 60% / 0.25)"
              strokeWidth="0.5"
            />
            {generateWindows(b, 9, 20).map(([wx, wy], j) => (
              <motion.rect
                key={j}
                x={b.x + wx}
                y={340 - b.h + wy}
                width="8"
                height="7"
                rx="1"
                fill="hsl(45 90% 70%)"
                initial={{
                  opacity: 0.12
                }}
                animate={{
                  opacity: [0.12, 0.5, 0.12]
                }}
                transition={{
                  duration: 3 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                }}
              />
            ))}
          </g>
        ))}

        {/* Mid buildings */}
        {midBuildings.map((b, i) => (
          <g key={`mid-${i}`}>
            <rect
              x={b.x}
              y={340 - b.h}
              width={b.w}
              height={b.h}
              fill="url(#buildingGradMid)"
              rx="2"
              stroke="hsl(263 55% 50% / 0.2)"
              strokeWidth="0.5"
            />
            {generateWindows(b, 10, 18).map(([wx, wy], j) => (
              <motion.rect
                key={j}
                x={b.x + wx}
                y={340 - b.h + wy}
                width="9"
                height="8"
                rx="1"
                fill="hsl(45 90% 65%)"
                initial={{
                  opacity: 0.15
                }}
                animate={{
                  opacity: [0.15, 0.7, 0.15]
                }}
                transition={{
                  duration: 2.5 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 2.5,
                }}
              />
            ))}
          </g>
        ))}

        {/* Front buildings - darkest, tallest */}
        {frontBuildings.map((b, i) => (
          <g key={`front-${i}`}>
            <rect
              x={b.x}
              y={340 - b.h}
              width={b.w}
              height={b.h}
              fill="url(#buildingGradFront)"
              rx="2"
              stroke="hsl(263 60% 50% / 0.2)"
              strokeWidth="0.5"
            />
            {/* Subtle edge highlight on left side */}
            <rect
              x={b.x}
              y={340 - b.h}
              width="1.5"
              height={b.h}
              fill="hsl(263 50% 55% / 0.3)"
              rx="0.5"
            />
            {generateWindows(b).map(([wx, wy], j) => (
              <motion.rect
                key={j}
                x={b.x + wx}
                y={340 - b.h + wy}
                width="11"
                height="9"
                rx="1"
                fill="#FBBF24"
                initial={{
                  opacity: 0.15
                }}
                animate={{
                  opacity: [0.15, 0.85, 0.15]
                }}
                transition={{
                  duration: 2 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </g>
        ))}
        </g>
        
        {/* Blending overlay at the very bottom, rendered on top to hide the base edge */}
        <rect x="0" y="325" width="570" height="15" fill="url(#bottomOverlayGrad)" />
      </svg>
    </div>
  );
};

export default BuildingSkyline;
